import { useState, useCallback, useMemo, useEffect } from 'react';
import _debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import { cleanDate } from '../../../../utils/helpers';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { generateValidationSchema } from '../utils/validationUtils';
import { FIELD_CONFIGS, getFieldsByUserType } from '../config/formConfig';
import { dispatch } from '../../../../redux/store';
import { ClubApi } from '../../../../services';
import { PlayerPosition, PreferredFoot } from '../../../../utils/constants';

export const useEditProfileForm = (userInfo, navigate) => {
    const [teams, setTeams] = useState([]);
    const [listOfNationalities, setListOfNationalities] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectTeam, setSelectTeam] = useState({});
    const [selectClub, setSelectClub] = useState({});
    const [isFetchingTeams, setIsFetchingTeams] = useState(false);
    const [errorMessage, setNotFoundClubError] = useState(false);

    const loadingUserUpdate = useSelector(
        ({ loading }) => loading.effects.user.updateUser
    );

    const { teamsModel } = useSelector(({ team }) => ({
        teamsModel: team.allTeams
    }));

    // Computed values
    const dateThreshold = useMemo(
        () => moment().subtract(5, 'years').format('YYYY-MM-DD'),
        []
    );

    const initialValues = useMemo(() => {
        // Only create initial values if userInfo exists to avoid dirty state issues
        if (!userInfo) {
            return {
                firstName: '',
                birthday: '',
                lastName: '',
                preferredFoot: '',
                secondaryPositions: [],
                contractStatus: '',
                nationalEligibility: [],
                openToTrials: false,
                height: '',
                clubName: '',
                teamName: '',
                location: '',
                passports: [],
                bio: '',
                gender: '',
                position: '',
                clubId: '',
                nonPlayerRole: [],
                teamId: '',
                contractExpiry: '',
                representedBy: ''
            };
        }

        return {
            firstName: userInfo.firstName || '',
            birthday: userInfo.birthday || '',
            lastName: userInfo.lastName || '',
            preferredFoot: userInfo.preferredFoot || '',
            secondaryPositions: Array.isArray(userInfo.secondaryPositions)
                ? userInfo.secondaryPositions
                : [],
            contractStatus: userInfo.contractStatus || '',
            nationalEligibility: Array.isArray(userInfo.nationalEligibility)
                ? userInfo.nationalEligibility
                : [],
            openToTrials: userInfo.openToTrials === true,
            height: userInfo.height || '',
            clubName: userInfo.clubName || '',
            teamName: userInfo.teamName || '',
            location: userInfo.location || '',
            bio: userInfo.bio || '',
            gender: userInfo.gender || '',
            position: userInfo.position || '',
            clubId: userInfo.clubId || '',
            nonPlayerRole: Array.isArray(userInfo.nonPlayerRole)
                ? userInfo.nonPlayerRole
                : [],
            teamId: userInfo.teamId || '',
            passports: Array.isArray(userInfo.passports)
                ? userInfo.passports
                : [],
            contractExpiry: userInfo.contractExpiry || '',
            representedBy: userInfo.representedBy || ''
        };
    }, [userInfo]);

    // Event handlers
    const handleTeamChange = useCallback(
        ({ target }) => {
            setSelectTeam(
                teams.find(item =>
                    item?.teamName
                        ?.toLowerCase()
                        ?.includes(target.value?.toLowerCase())
                ) || {}
            );
        },
        [teams]
    );

    const handleClubSelection = useCallback(async clubId => {
        setIsFetchingTeams(true);
        const teamsInGivenClub = [];
        const res = await dispatch.team.getTeamsByClubId(clubId);
        teamsInGivenClub.push(...res?.data);

        if (teamsInGivenClub?.length === 1) {
            setSelectTeam(teamsInGivenClub[0]);
        }
        setTeams(teamsInGivenClub);
        setIsFetchingTeams(false);
    }, []);

    const getTeamsBasedOnSelectedClub = useCallback(
        _debounce(handleClubSelection, 100),
        [handleClubSelection]
    );

    /**
     * Utility function to clean form values based on user type
     * @deprecated This function is deprecated and will be removed in future versions.
     */
    const cleanFormValuesByUserType = useCallback((values, userType) => {
        if (!userType) return values;

        // Get allowed fields for the user type
        const allowedFields = getFieldsByUserType(userType);

        // Create a new object with only allowed fields
        const cleanedValues = {};

        // Add allowed fields from form values
        allowedFields.forEach(fieldName => {
            if (values.hasOwnProperty(fieldName)) {
                cleanedValues[fieldName] = values[fieldName];
            }
        });

        // Always include common fields that might not be in FIELD_CONFIGS
        const alwaysIncludeFields = [
            'id',
            'teamId',
            'clubId',
            'clubName',
            'teamName'
        ];
        alwaysIncludeFields.forEach(fieldName => {
            if (values.hasOwnProperty(fieldName)) {
                cleanedValues[fieldName] = values[fieldName];
            }
        });

        return cleanedValues;
    }, []);

    const onSubmit = useCallback(
        async values => {
            // Clean values based on user type first
            const cleanedValues = cleanFormValuesByUserType(
                values,
                userInfo?.userType
            );

            const mergedValues = {
                ...(userInfo.userType === 'PLAYER' ? values : cleanedValues), // Use full values for PLAYER, cleaned for NON_PLAYER becasuse fields like 'position' are mandatory in PLAYER
                id: userInfo.id,
                teamId: selectTeam?.id || values.teamId,
                clubId: selectClub?.id || values.clubId
                // clubName: selectClub?.clubName || values?.clubName,
                // teamName: selectTeam?.teamName || values.teamName
            };

            // Uncomment when ready to save
            const res = await dispatch.user.updateUser(mergedValues);
            if (res === 1) {
                await Promise.all([
                    await dispatch.user.getUserProfile({ id: userInfo?.id }),
                    await dispatch.user.userSearchByProjection()
                ]);
                navigate('/profile');
            }
        },
        [
            userInfo,
            selectTeam,
            selectClub,
            teams,
            errorMessage,
            searchValue,
            navigate,
            cleanFormValuesByUserType
        ]
    );

    // Generate validation schema based on user type and club selection
    const validationSchema = useMemo(() => {
        return generateValidationSchema(
            FIELD_CONFIGS,
            userInfo?.userType,
            selectClub
        );
    }, [userInfo?.userType, selectClub]);

    // Formik setup
    const formik = useFormik({
        initialValues,
        onSubmit,
        validationSchema: validationSchema,
        enableReinitialize: true
    });

    // Create our own dirty check that's more reliable
    const isFormActuallyDirty = useMemo(() => {
        if (!userInfo?.id) return false;

        const differences = Object.keys(formik.values).filter(
            key =>
                JSON.stringify(formik.initialValues[key]) !==
                JSON.stringify(formik.values[key])
        );

        return differences.length > 0;
    }, [formik.values, formik.initialValues, userInfo?.id]);

    const { setFieldValue } = formik;

    const handleRoleChange = useCallback(
        (options, fieldName) => {
            // Handle different field types properly
            if (fieldName === 'preferredFoot' || fieldName === 'position') {
                // For single-select fields, use the value directly or empty string if cleared
                const newValue = options || '';
                if (formik.values[fieldName] !== newValue) {
                    setFieldValue(fieldName, newValue);
                    formik.setFieldTouched(fieldName, true);
                }
            } else {
                // For multi-select fields (like arrays), use the options array
                const newValue = Array.isArray(options)
                    ? options
                    : options
                      ? [options]
                      : [];
                if (
                    JSON.stringify(formik.values[fieldName]) !==
                    JSON.stringify(newValue)
                ) {
                    setFieldValue(fieldName, newValue);
                    formik.setFieldTouched(fieldName, true);
                }
            }
        },
        [setFieldValue, formik.values, formik.setFieldTouched]
    );

    const getListOfClubs = useCallback(async () => {
        await dispatch.club.getClubsByGenderAndType({
            gender: formik.values.gender || userInfo?.gender,
            ...(userInfo?.userType === 'PLAYER' && { type: 'DOMESTIC' })
        });
    }, [formik.values.gender, userInfo?.gender, userInfo?.userType]);

    const getListOfNationalities = useCallback(async () => {
        const res = await ClubApi.getNationalities({
            gender: formik.values?.gender || userInfo?.gender,
            type: 'NATIONAL'
        });
        setListOfNationalities(
            res?.data?.data
                ?.map(item => ({
                    ...item,
                    url: item?.clubLogoUrl,
                    name: item?.clubName
                }))
                ?.sort((a, b) => a.name.localeCompare(b.name)) || []
        );
        return res?.data || [];
    }, [formik?.values?.gender, userInfo?.gender]);

    // Initialize selectClub and selectTeam from userInfo only once
    useEffect(() => {
        if (userInfo?.clubName && !selectClub.clubName) {
            setSelectClub({
                id: userInfo.clubId || '',
                clubName: userInfo.clubName
            });
        }
        if (userInfo?.teamName && !selectTeam.teamName) {
            setSelectTeam({
                id: userInfo.teamId || '',
                teamName: userInfo.teamName
            });
        }
    }, [
        userInfo?.clubName,
        userInfo?.teamName,
        userInfo?.clubId,
        userInfo?.teamId
    ]);

    useEffect(() => {
        getListOfNationalities();
        getListOfClubs();
    }, [getListOfNationalities, getListOfClubs]);

    return {
        // Form state
        formik,
        isFormActuallyDirty,

        // Local state
        teams,
        setTeams,
        searchValue,
        setSearchValue,
        selectTeam,
        setSelectTeam,
        selectClub,
        setSelectClub,
        isFetchingTeams,
        errorMessage,
        setNotFoundClubError,

        // Computed values
        dateThreshold,
        loadingUserUpdate,
        teamsModel,

        // Event handlers
        handleTeamChange,
        handleRoleChange,
        getTeamsBasedOnSelectedClub,

        // Utils
        PlayerPosition,
        PreferredFoot,
        cleanDate,

        listOfNationalities
    };
};
